import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GoBadge } from "@/components/sistema/GoBadge";

type Crit = { criterion_id: string; label: string; target: string; current_value: string | null; status: string; last_check: string | null };
type Cap = { capability: string; status: "GA" | "BETA" | "EXPERIMENTAL"; pitch_text: string; restrictions: string | null };
type Met = { metric_id: string; label: string; baseline: number | null; target_30d: number | null; target_90d: number | null; current: number | null; trend: string | null };

const STATUS_CLASS: Record<string, string> = {
  green: "bg-primary/15 text-primary border-primary/30",
  yellow: "bg-warning/15 text-warning-foreground border-warning/30",
  red: "bg-destructive/15 text-destructive border-destructive/30",
  unknown: "bg-muted text-muted-foreground border-border",
};

const CAP_CLASS: Record<string, string> = {
  GA: "bg-primary/15 text-primary border-primary/30",
  BETA: "bg-warning/15 text-warning-foreground border-warning/30",
  EXPERIMENTAL: "bg-accent/15 text-accent-foreground border-accent/30",
};

export default function SistemaProntidaoComercial() {
  const { user } = useAuth();
  const [crit, setCrit] = useState<Crit[]>([]);
  const [caps, setCaps] = useState<Cap[]>([]);
  const [met, setMet] = useState<Met[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: m, error: mErr } = await supabase.from("org_members")
          .select("org_id").eq("user_id", user.id).limit(1).maybeSingle();
        if (mErr) throw mErr;
        if (!m || cancelled) return;
        const [c, ca, mt] = await Promise.all([
          supabase.from("commercial_go_criteria").select("*").eq("org_id", m.org_id).order("criterion_id"),
          supabase.from("commercial_capabilities").select("*").eq("org_id", m.org_id),
          supabase.from("commercial_metrics").select("*").eq("org_id", m.org_id).order("metric_id"),
        ]);
        if (c.error) throw c.error;
        if (ca.error) throw ca.error;
        if (mt.error) throw mt.error;
        if (cancelled) return;
        setCrit((c.data as Crit[]) ?? []);
        setCaps((ca.data as Cap[]) ?? []);
        setMet((mt.data as Met[]) ?? []);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Falha ao carregar dados comerciais");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  return (
    <div className="container mx-auto p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Prontidão comercial</h1>
          <p className="text-muted-foreground">6 critérios · matriz GA/Beta/Experimental · 9 métricas pós-GO</p>
        </div>
        <GoBadge />
      </div>

      <Tabs defaultValue="go" className="w-full">
        <TabsList>
          <TabsTrigger value="go">GO Status</TabsTrigger>
          <TabsTrigger value="matrix">Matriz comercial</TabsTrigger>
          <TabsTrigger value="post">Métricas pós-GO</TabsTrigger>
        </TabsList>

        <TabsContent value="go" className="space-y-3">
          {crit.map((c) => (
            <Card key={c.criterion_id}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-semibold">{c.criterion_id} · {c.label}</div>
                  <div className="text-xs text-muted-foreground">Alvo: {c.target}</div>
                  {c.current_value && <div className="text-xs mt-1">Atual: {c.current_value}</div>}
                </div>
                <Badge className={STATUS_CLASS[c.status] ?? STATUS_CLASS.unknown}>{c.status.toUpperCase()}</Badge>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="matrix" className="space-y-3">
          {caps.map((c) => (
            <Card key={c.capability}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  {c.capability}
                  <Badge variant="outline" className={CAP_CLASS[c.status]}>{c.status}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-1">
                <p>{c.pitch_text}</p>
                {c.restrictions && <p className="text-xs text-warning-foreground">Restrição: {c.restrictions}</p>}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="post">
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="border-b border-border">
                  <tr className="text-left">
                    <th className="p-3">ID</th><th className="p-3">Métrica</th>
                    <th className="p-3">Baseline</th><th className="p-3">30d</th>
                    <th className="p-3">90d</th><th className="p-3">Atual</th>
                  </tr>
                </thead>
                <tbody>
                  {met.map((m) => (
                    <tr key={m.metric_id} className="border-b border-border">
                      <td className="p-3 font-mono">{m.metric_id}</td>
                      <td className="p-3">{m.label}</td>
                      <td className="p-3">{m.baseline ?? "—"}</td>
                      <td className="p-3">{m.target_30d ?? "—"}</td>
                      <td className="p-3">{m.target_90d ?? "—"}</td>
                      <td className="p-3 font-semibold">{m.current ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
